import { useEffect, useState } from 'react';

import { Collection, MeetingRoomsResponse, UseMeetingRoomsReturn } from '../types/meetingTypes';
import { useUser } from 'global/UserContext';
import { useUserId } from 'hooks/useCurrentUser';
import { apiStrapi, setupStrapiHeaders } from 'services/apiService';
import { logger } from 'utils/logger';

// In-memory cache
let meetingRoomsCache: {
  data: Collection[] | null;
  timestamp: number;
  filter?: string;
  error?: string | null;
  status?: 'success' | 'not_found' | 'error';
  lastErrorTimestamp?: number;
} = {
  data: null,
  timestamp: 0,
  filter: undefined,
  error: null,
  status: 'success',
  lastErrorTimestamp: 0,
};

// Cache expiration time (5 minutes)
const CACHE_EXPIRATION = 5 * 60 * 1000;
// Error cache expiration time (1 minute)
const ERROR_CACHE_EXPIRATION = 1 * 60 * 1000;

// Function to transform room data into the format expected by JitsiVideoEmbed
const transformRoom = (room: any) => ({
  ...room,
  room_data: {
    id: room.id,
    title: room.title,
    vendor_code: room.vendor_code,
    vendor_room_id: room.key,
    organization_id: '',
    is_open: true,
    description: '',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    room_pwd: '',
    key: room.key,
    position: 0,
  },
  participation_status: {
    is_participant: true,
    role: 'participant',
  },
});

// Function to validate collections
const validateCollections = (collections: Collection[]): { isValid: boolean; error?: string } => {
  if (!collections || !Array.isArray(collections)) {
    return { isValid: false, error: 'Invalid rooms format' };
  }

  // Check that all collections have the required properties
  for (const collection of collections) {
    if (!collection.feature || !collection.rooms) {
      return { isValid: false, error: 'Required properties not found in rooms' };
    }
  }

  return { isValid: true };
};

// Function to get user-friendly error message
const getErrorMessage = (error: any): { message: string; status: 'not_found' | 'error' } => {
  if (error?.response?.status === 404) {
    return {
      message: 'No meeting rooms registered for your user.',
      status: 'not_found',
    };
  }

  if (error?.response?.status === 403) {
    return {
      message: 'You do not have permission to access the meeting rooms.',
      status: 'error',
    };
  }

  return {
    message: 'Could not load meeting rooms. Please try again later.',
    status: 'error',
  };
};

export const useMeetingRooms = (filter?: string): UseMeetingRoomsReturn => {
  const userId = useUserId();
  const { user, token } = useUser();
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(true);
  const [status, setStatus] = useState<'success' | 'not_found' | 'error'>('success');

  // Early authentication check - prevent any API setup if not authenticated
  // User is considered authenticated when both user.id and token exist
  const isFullyAuthenticated = Boolean(userId && token);

  useEffect(() => {
    // Guard: Don't proceed at all if user is not fully authenticated
    if (!isFullyAuthenticated) {
      logger.debug('[useMeetingRooms] Skipping fetch - user not fully authenticated', {
        hasUserId: !!userId,
        hasToken: !!token,
      });
      setLoading(false);
      setCollections([]);
      return;
    }

    const fetchMeetingRooms = async () => {
      // Double-check guard inside async function
      if (!userId || !token) {
        logger.debug('[useMeetingRooms] Skipping fetch - user not authenticated');
        setLoading(false);
        return;
      }

      try {
        const now = Date.now();

        // Extra protection: ensure Strapi headers
        const instance = apiStrapi.getInstance();
        const headers = instance.defaults.headers.common;
        const hasToken = !!headers['Authorization'];
        const hasUserPool = !!headers['X-Cognito-UserPool'];
        const hasOrgUUID = !!headers['X-Organization-UUID'];
        if (!hasToken || !hasUserPool || !hasOrgUUID) {
          // Try to set headers again
          const storedToken = localStorage.getItem('AUTH_TOKEN') || undefined;
          const orgUUID = localStorage.getItem('x-organization-uuid') || undefined;
          const userPool = localStorage.getItem('x-cognito-userpool') || undefined;
          if (!storedToken || !orgUUID || !userPool) {
            logger.debug('[useMeetingRooms] Missing required headers, skipping fetch');
            setLoading(false);
            return;
          }
          setupStrapiHeaders(storedToken, orgUUID, userPool);
        }

        // Check if we have a recent error in cache
        if (
          meetingRoomsCache.error &&
          meetingRoomsCache.lastErrorTimestamp &&
          now - meetingRoomsCache.lastErrorTimestamp < ERROR_CACHE_EXPIRATION
        ) {
          setError(meetingRoomsCache.error);
          setIsValid(false);
          setStatus(meetingRoomsCache.status || 'error');
          setCollections([]);
          setLoading(false);
          return;
        }

        // Check if we have valid cached data with the same filter
        if (
          meetingRoomsCache.data &&
          now - meetingRoomsCache.timestamp < CACHE_EXPIRATION &&
          meetingRoomsCache.filter === filter &&
          !meetingRoomsCache.error
        ) {
          setCollections(meetingRoomsCache.data);
          setError(null);
          setIsValid(true);
          setStatus(meetingRoomsCache.status || 'success');
          setLoading(false);
          return;
        }

        setLoading(true);

        const response = await apiStrapi.get<MeetingRoomsResponse>('/organization/meeting-rooms');

        // Validate that the response has the expected format
        if (!response.data || !response.data.collections) {
          throw new Error('Invalid API response format');
        }

        // Transform data to include the required fields
        let transformedCollections = response.data.collections.map((collection: Collection) => ({
          ...collection,
          rooms: collection.rooms.map(transformRoom),
        }));

        // Apply filter if provided
        if (filter) {
          transformedCollections = transformedCollections.filter(
            (collection: Collection) => collection.feature.name === filter
          );
        }

        // Validate the transformed collections
        const validation = validateCollections(transformedCollections);
        if (!validation.isValid) {
          throw new Error(validation.error || 'Invalid rooms data');
        }

        // Update cache
        meetingRoomsCache = {
          data: transformedCollections,
          timestamp: now,
          filter,
          error: null,
          status: 'success',
        };

        setCollections(transformedCollections);
        setError(null);
        setIsValid(true);
        setStatus('success');
      } catch (err) {
        const { message, status: errorStatus } = getErrorMessage(err);
        setError(message);
        setIsValid(false);
        setStatus(errorStatus);

        // Update cache with error
        meetingRoomsCache = {
          ...meetingRoomsCache,
          error: message,
          status: errorStatus,
          lastErrorTimestamp: Date.now(),
        };
      } finally {
        setLoading(false);
      }
    };

    fetchMeetingRooms();
  }, [filter, userId, token, isFullyAuthenticated, user]);

  return { collections, loading, error, isValid, status };
};

// Function to manually clear the cache (useful when we need to force a refresh)
export const clearMeetingRoomsCache = () => {
  meetingRoomsCache = {
    data: null,
    timestamp: 0,
    filter: undefined,
    error: null,
    status: 'success',
    lastErrorTimestamp: 0,
  };
};
